import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { PhoneMask } from 'src/app/order/masks/phone-mask';
import { Messages } from 'src/app/order/messages/order.messages';
import { ProposalService } from 'src/app/order/services/external/proposal/proposal.service';
import { DialogService } from 'src/app/order/services/internal/dialog/dialog.service';
import { LoadingService } from 'src/app/order/services/internal/loading/loading.service';
import { ScrollService } from 'src/app/order/services/internal/scroll/scroll.service';
import { WizardService } from 'src/app/order/services/internal/wizard/wizard.service';
import { DataStore } from 'src/app/order/store/data.store';
import { Proposal } from 'src/app/shared/models/proposal';
import { FdFieldConfigs } from 'src/app/shared/shared-components.module';
import { ConfigurationService } from 'src/app/start/services/configuration.service';
import { FormStep } from '../../../form-step';
import { RoutingService } from 'src/app/order/services/internal/routing/routing.service';
import { DataStoreService } from 'src/app/order/store/data-store.service';
import {
  EcommerceComplementaryInfoModel,
  TechnicalContactModel,
} from '../models/complementary-info.model';
import { EcommerceTechTypeEnum } from 'src/app/order/enums/ecommerce-tech-type.enum';
import { customTechnicalContactValidators } from './technical-contact.validators';

@Component({
  selector: 'app-technical-contact',
  templateUrl: './technical-contact.component.html',
  styleUrls: ['./technical-contact.component.scss'],
  standalone: false
})
export class TechnicalContactComponent extends FormStep implements OnInit {
  public STEP_NUMBER = 0;

  public techType: EcommerceTechTypeEnum;

  public technicalContactModel: TechnicalContactModel;

  public ecommerceComplementaryInfoModel: EcommerceComplementaryInfoModel;

  public currentEmail: string;

  constructor(
    private formBuilder: FormBuilder,
    protected proposalService: ProposalService,
    protected loadingService: LoadingService,
    protected dialogService: DialogService,
    protected dataStore: DataStore,
    protected scrollService: ScrollService,
    protected wizardService: WizardService,
    protected matDialog: MatDialog,
    protected configurationService: ConfigurationService,
    protected routingService: RoutingService,
    protected dataStoreService: DataStoreService,
    private route: ActivatedRoute
  ) {
    super(
      dataStore,
      scrollService,
      wizardService,
      matDialog,
      proposalService,
      loadingService,
      dialogService,
      configurationService,
      routingService,
      dataStoreService
    );
  }

  public async ngOnInit() {
    this.techType = this.route.snapshot.data.techType;
    await super.ngOnInit();
  }

  public createFields(): FdFieldConfigs {
    return {
      name: {
        label: 'Nome do Contato',
        controlName: 'name',
        maxLength: 50,
        messages: {
          required: 'Informe o nome completo',
          invalid: 'Informe o nome completo',
        },
      },
      email: {
        label: 'E-mail',
        controlName: 'email',
        messages: {
          required: 'Informe o e-mail',
          pattern: 'E-mail invalido',
        },
      },
      primaryPhone: {
        label: 'Telefone 1',
        controlName: 'primaryPhone',
        mask: PhoneMask,
        maskCharsReplace: /[-( )]/g,
        messages: {
          required: 'Informe o telefone',
          invalid: 'Telefone invalido',
          pattern: 'Telefone invalido',
        },
      },
      secondaryPhone: {
        label: 'Telefone 2',
        controlName: 'secondaryPhone',
        mask: PhoneMask,
        maskCharsReplace: /[-( )]/g,
        messages: {
          invalid: 'Telefone invalido',
          pattern: 'Telefone invalido',
        },
      },
      mobileNumber: {
        label: 'Celular',
        controlName: 'mobileNumber',
        mask: PhoneMask,
        maskCharsReplace: /[-( )]/g,
        messages: {
          required: 'Informe o celular',
          invalid: 'Celular invalido',
          pattern: 'Celular invalido',
        },
      },
    };
  }

  public createFormGroup() {
    return this.formBuilder.group({
      name: ['', customTechnicalContactValidators.name],
      email: ['', customTechnicalContactValidators.email],
      primaryPhone: ['', customTechnicalContactValidators.primaryPhone],
      secondaryPhone: ['', customTechnicalContactValidators.secondaryPhone],
      mobileNumber: ['', customTechnicalContactValidators.mobileNumber],
    });
  }

  public emailChange(email: string) {
    this.currentEmail = email;
  }

  public checkEmail() {
    if (this.isFormGroupValid()) {
      this.dialogService.openConfirmDialogWithHtml(
        new Messages(
          'O e-mail do contato esta correto?',
          `Por favor, confirme se o e-mail digitado esta correto: <br> <br> <b> ${this.currentEmail} </b>`
        ),
        true,
        () => this.next()
      );
    }
  }

  public next(): void {
    this.submitted = true;

    if (this.isFormGroupValid()) {
      this.dataStore.setStepStatus(this.STEP_NUMBER, this.formGroup.valid);
      this.persistData().then((persisted) => {
        if (persisted) {
          this.wizardService.nextStep();
        }
      });
    }
  }

  protected proposalGetCallback(): void {}

  protected persistData() {
    const technicalContact: TechnicalContactModel = {
      ...this.formGroup.getRawValue(),
    };

    const technicalContactComplementaryInfo: EcommerceComplementaryInfoModel = {
      ...this.ecommerceComplementaryInfoModel,
      technicalContact,
    };

    const ecommerceComplementaryInfo = (this.proposal?.ecommerceComplementaryInfo || [])
      .filter((complementaryInfo) => complementaryInfo.techType !== this.techType);

    ecommerceComplementaryInfo.push(technicalContactComplementaryInfo);

    return this.dataStore.updateProposal({ ecommerceComplementaryInfo });
  }

  protected updateFields(proposal: Proposal) {
    if (proposal.ecommerceComplementaryInfo) {
      this.ecommerceComplementaryInfoModel = proposal.ecommerceComplementaryInfo
        .filter((complementaryInfo) => complementaryInfo.techType == this.techType)
        .pop();
    }

    this.technicalContactModel = this.ecommerceComplementaryInfoModel?.technicalContact;

    if (!this.technicalContactModel) {
      return;
    }

    this.formControls.name.setValue(this.technicalContactModel.name);
    this.formControls.email.setValue(this.technicalContactModel.email);
    this.formControls.primaryPhone.setValue(this.technicalContactModel.primaryPhone);
    this.formControls.secondaryPhone.setValue(this.technicalContactModel.secondaryPhone);
    this.formControls.mobileNumber.setValue(this.technicalContactModel.mobileNumber);
    this.emailChange(this.technicalContactModel.email);
  }
}
